import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Plus, Trash2, AlertTriangle, Info, Wand2 } from "lucide-react";
import { toast } from "sonner";

const UNMAPPED = "__unmapped__";

/**
 * Placeholder editor — click a template, review every raw `<tag>` / `{{tag}}`
 * detected in the master, and choose which canonical client field it maps
 * to (or mark it as unmapped, or add a custom placeholder specific to
 * this template).
 */
export default function PlaceholderEditorDialog({ template, open, onOpenChange, onSaved }) {
  const [catalog, setCatalog] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [custom, setCustom] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/templates/placeholders")
      .then((r) => setCatalog(r.data))
      .catch(() => toast.error("Failed to load placeholder catalog"));
    setOverrides({ ...(template?.placeholder_overrides || {}) });
    // Always regenerate a fresh client-only _uid on each open so nothing the
    // server sends can collide with an in-memory row.
    setCustom((template?.custom_placeholders || []).map((cp, i) => ({
      ...cp,
      _uid: `cp-load-${Date.now()}-${i}`,
    })));
  }, [open, template]);

  // Flatten every raw variant we've detected — known + unknown — into one
  // pretty table.
  const detectedRows = useMemo(() => {
    if (!template) return [];
    const rows = [];
    for (const p of template.placeholders_known || []) {
      for (const raw of p.raw_variants || []) {
        rows.push({ raw, autoCanonical: p.canonical, count: p.count });
      }
    }
    for (const raw of template.placeholders_unknown || []) {
      rows.push({ raw, autoCanonical: null, count: null });
    }
    return rows.sort((a, b) => a.raw.localeCompare(b.raw));
  }, [template]);

  function setOverride(raw, value) {
    const key = raw.trim().toLowerCase();
    setOverrides((o) => ({ ...o, [key]: value }));
  }
  function effectiveMapping(row) {
    const key = row.raw.trim().toLowerCase();
    if (key in overrides) return overrides[key];
    if (row.autoCanonical) return row.autoCanonical;
    return UNMAPPED;
  }
  function addCustom() {
    // Attach a stable UID so React keys don't shift when a row is removed.
    // `_uid` is stripped before sending to the backend.
    setCustom((c) => [...c, { _uid: `cp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, key: "", label: "", default_value: "" }]);
  }
  function updateCustom(i, patch) {
    setCustom((c) => c.map((cp, idx) => idx === i ? { ...cp, ...patch } : cp));
  }
  function removeCustom(i) {
    setCustom((c) => c.filter((_, idx) => idx !== i));
  }

  async function submit() {
    // Validate custom keys
    for (const cp of custom) {
      if (!cp.key || !/^[a-z][a-z0-9_]*$/.test(cp.key)) {
        return toast.error(`Custom key '${cp.key}' invalid — use lowercase letters, digits and _`);
      }
    }
    setSaving(true);
    try {
      await api.put(`/templates/${template.id}/placeholder-map`, {
        placeholder_overrides: overrides,
        // Strip client-only `_uid` before sending — backend expects only
        // {key, label, default_value}.
        custom_placeholders: custom.map(({ _uid, ...rest }) => rest),
      });
      toast.success("Placeholder mapping saved");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  }

  if (!template) return null;
  const customKeys = new Set(custom.map((c) => c.key).filter(Boolean));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="placeholder-editor-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0D1B2A]">Placeholder map — {template.name}</DialogTitle>
          <p className="text-xs text-slate-500 flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5" />
            {detectedRows.length} placeholder variant{detectedRows.length === 1 ? "" : "s"} detected. Overrides win over the built-in alias table.
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          <table className="w-full text-sm border-collapse" data-testid="placeholder-table">
            <thead className="text-[10px] uppercase tracking-widest text-slate-500 sticky top-0 bg-white z-10">
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 font-medium">Raw tag in template</th>
                <th className="text-left py-2 font-medium">Detected as</th>
                <th className="text-left py-2 font-medium">Maps to</th>
                <th className="text-right py-2 font-medium w-20">Count</th>
              </tr>
            </thead>
            <tbody>
              {detectedRows.map((row) => {
                const value = effectiveMapping(row);
                const isCustom = customKeys.has(value);
                return (
                  <tr key={row.raw} className="border-b border-slate-100" data-testid={`placeholder-row-${row.raw}`}>
                    <td className="py-2">
                      <code className="text-xs bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">
                        &lt;{row.raw}&gt;
                      </code>
                    </td>
                    <td className="py-2 text-xs">
                      {row.autoCanonical
                        ? <Badge variant="outline" className="text-[10px]">{row.autoCanonical}</Badge>
                        : <span className="text-rose-600 flex items-center gap-1 text-[11px]">
                            <AlertTriangle className="w-3 h-3" />unknown
                          </span>}
                    </td>
                    <td className="py-2">
                      <Select value={value} onValueChange={(v) => setOverride(row.raw, v)}>
                        <SelectTrigger className="h-8 w-64 text-xs" data-testid={`mapping-select-${row.raw}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          <SelectItem value={UNMAPPED}>— Unmapped (leave as-is) —</SelectItem>
                          {catalog.length > 0 && (
                            <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-slate-500">Canonical fields</div>
                          )}
                          {catalog.map((c) => (
                            <SelectItem key={c.key} value={c.key}>{c.key} — {c.label}</SelectItem>
                          ))}
                          {customKeys.size > 0 && (
                            <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-slate-500 border-t border-slate-100 mt-1">Custom fields</div>
                          )}
                          {[...customKeys].filter(Boolean).map((k) => (
                            <SelectItem key={k} value={k}>{k}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {isCustom && <span className="ml-1 text-[10px] text-[#1A7F6E]">custom</span>}
                    </td>
                    <td className="text-right py-2 text-xs text-slate-500">
                      {row.count ?? "—"}
                    </td>
                  </tr>
                );
              })}
              {detectedRows.length === 0 && (
                <tr><td colSpan={4} className="py-6 text-center text-sm text-slate-500">
                  No placeholder tags detected in this template.
                </td></tr>
              )}
            </tbody>
          </table>

          <div className="mt-6 border-t border-slate-200 pt-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><Wand2 className="w-3 h-3" />Custom placeholders</p>
                <p className="text-[11px] text-slate-500">Register a placeholder specific to this template (e.g. state authority name). Its default value gets inserted at generation time.</p>
              </div>
              <Button size="sm" variant="outline" onClick={addCustom} data-testid="add-custom-placeholder-btn">
                <Plus className="w-3.5 h-3.5 mr-1" />Add
              </Button>
            </div>
            {custom.length === 0 ? (
              <p className="text-xs text-slate-400 italic">No custom placeholders yet.</p>
            ) : (
              <ul className="space-y-2">
                {custom.map((cp, i) => (
                  <li key={cp._uid || i} className="grid grid-cols-12 gap-2 items-center" data-testid={`custom-row-${i}`}>
                    <div className="col-span-3">
                      <Label className="text-[10px] uppercase tracking-widest text-slate-500">Key</Label>
                      <Input value={cp.key} onChange={(e) => updateCustom(i, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })} placeholder="state_authority_name" data-testid={`custom-key-${i}`} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px] uppercase tracking-widest text-slate-500">Label</Label>
                      <Input value={cp.label} onChange={(e) => updateCustom(i, { label: e.target.value })} placeholder="State Authority Name" data-testid={`custom-label-${i}`} />
                    </div>
                    <div className="col-span-5">
                      <Label className="text-[10px] uppercase tracking-widest text-slate-500">Default value</Label>
                      <Input value={cp.default_value} onChange={(e) => updateCustom(i, { default_value: e.target.value })} placeholder="TAS Guardianship Board" data-testid={`custom-value-${i}`} />
                    </div>
                    <div className="col-span-1 flex items-end h-full pb-1">
                      <Button size="sm" variant="outline" onClick={() => removeCustom(i)} className="text-rose-600" data-testid={`custom-remove-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#1A7F6E] hover:bg-[#136053] text-white" data-testid="save-placeholder-map-btn">
            {saving ? "Saving…" : "Save mapping"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
